/**
 * A read-only link to this flight.
 *
 * The card exists because the share link is the answer to a question the app
 * gets asked constantly and used to answer only from an overflow menu: "send it
 * to my instructor". One line says exactly what the recipient gets — the flight,
 * without an account, without the ability to change it — because a link that
 * silently granted editing would be the worst surprise this app could produce.
 *
 * A flight that lives only in this browser cannot be shared, and the card says
 * so instead of offering a button that fails: there is nothing on the server to
 * point a link at. That is also the honest description of the signed-out mode —
 * everything works, nothing leaves the machine.
 *
 * There is no `Widerrufen` here yet. The API creates share tokens and does not
 * revoke them, and a button that only appeared to revoke one would be worse than
 * its absence.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import * as api from '../../api';
import { useT } from '../../i18n';

export function ShareCard() {
  const t = useT();
  /* the workspace is `/flights/:id/:tab?`; at `/` there is no id and no server
     copy — `useParams` is how a pane learns which flight it is in without the
     shell having to hand it down through the tab registry */
  const { id } = useParams<{ id?: string }>();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const share = await api.sharePlan(id);
      setUrl(window.location.origin + share.url_path);
      setCopied(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    void navigator.clipboard?.writeText(url).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  return (
    <section className="aex-card">
      <div className="aex-head">
        <h2 className="aex-title">{t('export.share.title')}</h2>
      </div>
      <p className="aex-line">{t('export.share.line')}</p>

      {!id ? (
        <p className="aex-state">{t('export.share.local')}</p>
      ) : url ? (
        <div className="aex-acts">
          <input className="ae-field ae-num aex-url" readOnly value={url} aria-label={t('export.share.title')} />
          <button type="button" className="ae-btn" onClick={copy}>
            {copied ? t('export.share.copied') : t('ed.copy')}
          </button>
        </div>
      ) : (
        <div className="aex-acts">
          <button type="button" className="ae-btn" disabled={busy} onClick={() => void create()}>
            {busy ? t('export.share.busy') : t('export.share.btn')}
          </button>
        </div>
      )}

      {error ? <p className="aex-err">{api.apiMessage(t, error)}</p> : null}
    </section>
  );
}
